import { SelectProvider } from 'context/select-context'
import classNames from 'helpers/class-names'
import React, { FC, ReactNode, useRef, useState } from 'react'
// FancyCombobox.tsx
import {
  FloatingFocusManager,
  FloatingList,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react'

interface Props {
  placeholderElement?: ReactNode
  placeholderText?: string
  children: ReactNode
  onSelect?: (item: string) => void
  value?: string
  variant?: 'default' | 'compact'
}

export const FancyCombobox: FC<Props & { value?: string }> = ({
  placeholderElement,
  placeholderText,
  children,
  onSelect,
  value,
  variant = 'default',
}) => {
  const [open, setOpen] = useState<boolean>(false)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [search, setSearch] = useState<string>('')
  const [internalValue, setInternalValue] = useState<string>(value ?? '')

  const inputRef = useRef<HTMLInputElement>(null)
  const elementsRef = useRef<Array<HTMLButtonElement | null>>([])
  const labelsRef = useRef<Array<string | null>>([])

  React.useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value)
    }
  }, [value])

  const filteredChildren = React.Children.toArray(children).filter((child) => {
    if (React.isValidElement(child) && (child.props as any).label) {
      return (child.props as any).label
        .toLowerCase()
        .includes(search.toLowerCase())
    }
    return false
  }) as React.ReactElement[]

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: (val) => {
      setOpen(val)
      if (!val) {
        setSearch('')
      }
    },
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start',
    middleware: [offset(0), flip({ crossAxis: false }), shift()],
  })

  const click = useClick(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'menu' })
  const listNavigation = useListNavigation(context, {
    listRef: elementsRef,
    onNavigate: setActiveIndex,
    activeIndex,
  })

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [role, dismiss, listNavigation, click],
  )

  const selectContext = React.useMemo(
    () => ({
      activeIndex,
      getItemProps,
      handleSelect: () => {
        setOpen(false)
        setSearch('')
      },
    }),
    [activeIndex, getItemProps],
  )

  const handleSelect = (item: string) => {
    setInternalValue(item)
    if (onSelect) {
      onSelect(item)
    }
    setOpen(false)
    setSearch('')
  }

  const dropdownClasses = classNames(
    'w-56 transition duration-300 ease-in-out font-body',
    {
      block: open,
      hidden: !open,
    },
  )

  return (
    <SelectProvider value={selectContext}>
      <div className="relative w-56 tracking-normal">
        {placeholderElement && !open ? (
          <div
            onClick={() => setOpen(true)}
            className={classNames(
              'h-7 w-full overflow-clip rounded border border-gray-100 bg-white font-body text-xs font-semibold text-gray-400',
              { 'h-5': variant === 'compact' },
            )}>
            {placeholderElement}
          </div>
        ) : (
          <input
            ref={(node) => {
              refs.setReference(node)
              inputRef.current = node
            }}
            className={classNames(
              'search-close-icon border-bottom-2 h-7 w-full rounded border border-gray-100 px-3 py-1 font-body text-xs font-semibold text-gray-400 outline-none placeholder:text-gray-200',
              {
                'h-5 px-2': variant === 'compact',
                'rounded-bl-none rounded-br-none': open,
              },
            )}
            type="text"
            placeholder={placeholderText || 'Search...'}
            value={open ? search : internalValue}
            onChange={(e) => {
              setSearch(e.target.value)
              setOpen(true)
            }}
            {...getReferenceProps({
              onClick: () => setOpen(true),
              onFocus: () => setOpen(true),
            })}
          />
        )}
        <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
          <FloatingPortal>
            <FloatingFocusManager context={context} initialFocus={inputRef}>
              <div
                ref={refs.setFloating}
                style={floatingStyles}
                className={classNames('w-56 outline-none', {
                  'pointer-events-none': !open,
                })}
                {...getFloatingProps()}>
                <div className={dropdownClasses}>
                  <div className="divide-gray-100 rounded rounded-tl-none rounded-tr-none border border-t-0 border-gray-100 bg-white text-base focus:outline-none dark:border-gray-800 dark:bg-gray-500 sm:text-sm">
                    <div className="py-1">
                      {filteredChildren.length > 0 && (
                        <ul className="hide-scrollbar max-h-72 overflow-scroll">
                          {filteredChildren.map((child, index) => (
                            <li
                              key={index}
                              onClick={() => {
                                if (
                                  React.isValidElement(child) &&
                                  (child.props as any).label
                                ) {
                                  handleSelect((child.props as any).label)
                                }
                              }}>
                              {child}
                            </li>
                          ))}
                        </ul>
                      )}
                      {filteredChildren.length === 0 && !search && (
                        <div className="w-full py-4 text-center text-xs text-gray-300">
                          No items
                        </div>
                      )}
                      {filteredChildren.length === 0 && search && (
                        <div className="w-full py-4 text-center text-xs text-gray-300">
                          No matches
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </FloatingFocusManager>
          </FloatingPortal>
        </FloatingList>
      </div>
    </SelectProvider>
  )
}
